import flatpickr from '../../vendor/flatpickr';

const VALUE_FORMATS = { date: 'Y-m-d', time: 'H:i', dateTime: 'Y-m-d H:i' };
const DISPLAY_FORMATS = { date: 'D, d M Y', time: 'h:i K', dateTime: 'D, d M Y - h:i K' };

/**
 * @param {boolean} dateOnly
 * @param {boolean} enableTime
 * @returns {'date'|'time'|'dateTime'}
 */
function resolveKind(dateOnly, enableTime) {
    if (dateOnly && !enableTime) {
        return 'date';
    }

    return !dateOnly && enableTime ? 'time' : 'dateTime';
}

/**
 * @param {number[]|null} enableDays Weekdays (0 = Sunday) that stay selectable.
 * @param {number[]|null} disableDays Weekdays that are blocked.
 */
function weekdayRules(enableDays, disableDays) {
    if (enableDays?.length > 0) {
        return { enable: [(date) => enableDays.includes(date.getDay())] };
    }

    if (disableDays?.length > 0) {
        return { disable: [(date) => disableDays.includes(date.getDay())] };
    }

    return {};
}

/**
 * Date / time / range input backed by flatpickr. Expects an `x-ref="input"` element and
 * dispatches `date-change` with the picked value (or `dateFrom` / `dateTo` in range mode).
 */
export default function datePicker({
    mode = 'single',
    enableDays = null,
    disableDays = null,
    enableTime = false,
    dateOnly = true,
    minDate = null,
    maxDate = null,
    defaultDate = null,
    displayFormat = null,
} = {}) {
    let picker = null;

    return {
        value: null,
        dateFrom: null,
        dateTo: null,

        init() {
            const kind = resolveKind(dateOnly, enableTime);

            picker = flatpickr(this.$refs.input, {
                mode,
                enableTime: enableTime || !dateOnly,
                noCalendar: !dateOnly,
                time_24hr: false,
                dateFormat: VALUE_FORMATS[kind],
                altInput: true,
                altFormat: displayFormat ?? DISPLAY_FORMATS[kind],
                minDate: minDate ?? undefined,
                maxDate: maxDate ?? undefined,
                defaultDate: defaultDate ?? undefined,
                allowInput: false,
                disableMobile: false,
                animate: true,
                ...weekdayRules(enableDays, disableDays),
                onChange: (selectedDates, dateStr, instance) => this.handleChange(selectedDates, dateStr, instance),
            });
        },

        destroy() {
            picker?.destroy();
        },

        handleChange(selectedDates, dateStr, instance) {
            if (mode === 'range') {
                const format = (date) => instance.formatDate(date, instance.config.dateFormat);

                this.dateFrom = selectedDates[0] ? format(selectedDates[0]) : null;
                this.dateTo = selectedDates[1] ? format(selectedDates[1]) : null;

                this.$dispatch('date-change', { dateFrom: this.dateFrom, dateTo: this.dateTo });

                return;
            }

            this.value = dateStr || null;

            this.$dispatch('date-change', { value: this.value });
        },

        clear() {
            picker?.clear();
        },

        setDate(date) {
            picker?.setDate(date);
        },
    };
}
